'use client';

import React, { useCallback, useRef } from 'react';
import { MapConfig } from '@/lib/mapConfigs';
import { RoundState, RoundResult } from './AimTrainerApp';
import TargetSilhouette from './TargetSilhouette';
import { CrosshairStyle } from './CrosshairSelector';
import { AdvancedHitbox, DEFAULT_ADVANCED_HITBOX } from './TargetSilhouette';

interface GameSceneProps {
  map: MapConfig;
  roundState: RoundState;
  roundResult?: RoundResult;
  onTargetClick: (zone: 'head' | 'body' | 'miss') => void;
  onSceneClick: () => void;
  targetScale?: number;
  showGun?: boolean;
  crosshairSize?: number;
  crosshairColor?: string;
  showCrosshair?: boolean;
  crosshairStyle?: CrosshairStyle;
  spawnOffsetX?: number;
  spawnOffsetY?: number;
  debugMode?: boolean;
  aimDetectionSource?: 'crosshair' | 'mouse';
  showDebugHitboxes?: boolean;
  characterSpeed?: number;
  travelDistance?: number;
  targetMotion?: 'pass-through' | 'stop-at-crosshair';
  spawnDirection?: 'left' | 'right';
  targetSkin?: string;
  targetSkinWeapon?: 'vandal' | 'phantom';
  advancedHitbox?: AdvancedHitbox;
  missedShotBehavior?: 'disappear' | 'leave';
  // Second target (Simulation Mode)
  secondTargetVisible?: boolean;
  secondTargetSpawnSide?: 'left' | 'right';
  secondRoundState?: RoundState;
  onSecondTargetClick?: (zone: 'head' | 'body' | 'miss') => void;
  secondTargetXPercent?: number;
}

// Base target body width (must match TargetSilhouette constants)
const BASE_BODY_W = 52;
// Base head height (must match TargetSilhouette constants)
const BASE_HEAD_H = 36;

// Tolerance margin in px added to head hitbox on each side for fair hit registration.
const HEAD_HIT_TOLERANCE_PX = 6;
// Smaller tolerance for body (it's already large)
const BODY_HIT_TOLERANCE_PX = 4;

/**
 * HEAD hitbox: circle-based distance check.
 */
function pointInCircle(
  x: number,
  y: number,
  el: HTMLElement,
  tolerance: number
): { hit: boolean; rect: DOMRect; cx: number; cy: number; radius: number; dist: number } {
  const r = el.getBoundingClientRect();
  let cx = r.left + r.width / 2;
  let cy = r.top + r.height / 2;
  const radius = Math.min(r.width, r.height) / 2;
  const dx = x - cx;
  const dy = y - cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const hit = dist <= radius + tolerance;
  return { hit, rect: r, cx, cy, radius, dist };
}

/**
 * BODY hitbox: rectangular bounds check.
 */
function pointInRect(
  x: number,
  y: number,
  el: HTMLElement,
  tolerance: number
): { hit: boolean; rect: DOMRect } {
  const r = el.getBoundingClientRect();
  const hit =
    x >= r.left - tolerance &&
    x <= r.right + tolerance &&
    y >= r.top - tolerance &&
    y <= r.bottom + tolerance;
  return { hit, rect: r };
}

export default function GameScene({
  map,
  roundState,
  roundResult,
  onTargetClick,
  onSceneClick,
  targetScale = 1.0,
  showGun = false,
  crosshairSize = 4,
  crosshairColor = '#FFFFFF',
  showCrosshair = false,
  crosshairStyle = 'dot',
  spawnOffsetX = 0,
  spawnOffsetY = 0,
  debugMode = false,
  aimDetectionSource = 'crosshair',
  showDebugHitboxes = false,
  characterSpeed = 200,
  travelDistance = 120,
  targetMotion = 'pass-through',
  spawnDirection,
  targetSkin,
  targetSkinWeapon,
  advancedHitbox = DEFAULT_ADVANCED_HITBOX,
  missedShotBehavior = 'disappear',
  secondTargetVisible = false,
  secondTargetSpawnSide,
  secondRoundState = 'waiting',
  onSecondTargetClick,
  secondTargetXPercent = 25,
}: GameSceneProps) {
  const isTargetVisible = roundState === 'spawning' || roundState === 'active' || (roundState === 'resolving' && (roundResult === 'miss' ? missedShotBehavior === 'leave' : false));
  const isSecondTargetVisible = secondTargetVisible && (secondRoundState === 'spawning' || secondRoundState === 'active');
  const isIcebox = map.id === 'map-icebox-b' || map.id === 'map-icebox-a' || map.id === 'icebox' || map.mapCode === 'ICEBOX';

  // Effective spawn side:
  // 1. spawnDirection setting (manual override) takes priority when explicitly set
  // 2. Falls back to map.wallSide (per-angle automatic wall geometry)
  // 3. Falls back to map.spawnSide for legacy configs
  const effectiveSpawnSide: 'left' | 'right' = spawnDirection ?? map.wallSide ?? map.spawnSide;

  // Second target always uses its own independent spawn side (NOT crosshair-aligned)
  const effectiveSecondSpawnSide: 'left' | 'right' = secondTargetSpawnSide ?? (effectiveSpawnSide === 'left' ? 'right' : 'left');

  // Uniform scale: targetScale only affects hitbox size, not visual size variance between maps.
  // All maps render at the same base size — targetScale is a user-controlled multiplier.
  const bodyW = BASE_BODY_W * targetScale;
  // Half the scaled head height — used to center the head at the crosshair y (50% viewport)
  // CRITICAL: scaling must anchor at the head/eye center point, not feet or center-mass.
  // The transform below positions the container so that the head center (top of container + headHalfH)
  // lands exactly at 50% viewport height (crosshair position).
  const headHalfH = (BASE_HEAD_H * targetScale) / 2;
  // X offset: center the body horizontally on the crosshair x (50% viewport)
  const headOffsetPx = bodyW / 2;

  // Primary target refs
  const headRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Second target refs
  const head2Ref = useRef<HTMLDivElement | null>(null);
  const body2Ref = useRef<HTMLDivElement | null>(null);

  // ── Visibility refs — always reflect the latest render state ─────────────
  // Using refs instead of closure-captured booleans prevents the race condition
  // where a click fires between two React state updates and reads stale
  // isTargetVisible / isSecondTargetVisible values from a previous render.
  const isTargetVisibleRef = useRef(false);
  const isSecondTargetVisibleRef = useRef(false);
  isTargetVisibleRef.current = isTargetVisible;
  isSecondTargetVisibleRef.current = isSecondTargetVisible;

  // Ref for roundState — used in logging only, avoids stale closure in handleSceneMouseDown
  const roundStateLogRef = useRef(roundState);
  roundStateLogRef.current = roundState;

  const handleSceneMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;

    const clickTime = Date.now();

    // Read visibility from refs — guaranteed fresh at click time, never stale.
    const primaryVisible = isTargetVisibleRef.current;
    const secondaryVisible = isSecondTargetVisibleRef.current;

    // Count active targets for logging
    const activeTargetCount = (primaryVisible ? 1 : 0) + (secondaryVisible ? 1 : 0);

    let cx: number;
    let cy: number;
    let coordSource: string;

    if (aimDetectionSource === 'crosshair') {
      cx = window.innerWidth / 2;
      cy = window.innerHeight / 2;
      coordSource = 'crosshair-center';
    } else {
      cx = e.clientX;
      cy = e.clientY;
      coordSource = 'mouse-cursor';
    }

    // ── Collect hit results for ALL currently active targets ─────────────────
    // We must check every active target's hitboxes before deciding the outcome.
    // Never short-circuit after the first non-match — a click that misses the
    // primary target may still land on the secondary target's hitbox.

    type TargetHit = {
      target: 'primary' | 'secondary';
      zone: 'head' | 'body';
      dist: number; // distance from click to hitbox center (head) or 0 for body
    };

    const hits: TargetHit[] = [];

    // Check primary target — read bounds fresh at click time via getBoundingClientRect()
    let primaryHeadResult: ReturnType<typeof pointInCircle> | null = null;
    let primaryBodyResult: ReturnType<typeof pointInRect> | null = null;

    if (primaryVisible && headRef.current && bodyRef.current) {
      // getBoundingClientRect() is called here, at click time — always fresh,
      // never from a cached/stale animation frame.
      primaryHeadResult = pointInCircle(cx, cy, headRef.current, HEAD_HIT_TOLERANCE_PX);
      primaryBodyResult = pointInRect(cx, cy, bodyRef.current, BODY_HIT_TOLERANCE_PX);

      if (primaryHeadResult.hit) {
        hits.push({ target: 'primary', zone: 'head', dist: primaryHeadResult.dist });
      } else if (primaryBodyResult.hit) {
        hits.push({ target: 'primary', zone: 'body', dist: 0 });
      }
    }

    // Check secondary target — always check regardless of primary result.
    // getBoundingClientRect() called fresh here too.
    let secondaryHeadResult: ReturnType<typeof pointInCircle> | null = null;
    let secondaryBodyResult: ReturnType<typeof pointInRect> | null = null;

    if (secondaryVisible && head2Ref.current && body2Ref.current && onSecondTargetClick) {
      secondaryHeadResult = pointInCircle(cx, cy, head2Ref.current, HEAD_HIT_TOLERANCE_PX);
      secondaryBodyResult = pointInRect(cx, cy, body2Ref.current, BODY_HIT_TOLERANCE_PX);

      if (secondaryHeadResult.hit) {
        hits.push({ target: 'secondary', zone: 'head', dist: secondaryHeadResult.dist });
      } else if (secondaryBodyResult.hit) {
        hits.push({ target: 'secondary', zone: 'body', dist: 0 });
      }
    }

    // ── Per-click diagnostic log ──────────────────────────────────────────────
    // Logs: active target count, which targets were hit (if any), final outcome.
    // Use this to distinguish geometry-math misses from state/locking drops.
    const primaryHitLabel = !primaryVisible
      ? 'NOT_VISIBLE'
      : primaryHeadResult?.hit
        ? 'HEAD_HIT'
        : primaryBodyResult?.hit
          ? 'BODY_HIT'
          : `MISS(head_dist=${primaryHeadResult?.dist.toFixed(1)},r=${primaryHeadResult?.radius.toFixed(1)})`;

    const secondaryHitLabel = !secondaryVisible
      ? 'NOT_VISIBLE'
      : secondaryHeadResult?.hit
        ? 'HEAD_HIT'
        : secondaryBodyResult?.hit
          ? 'BODY_HIT'
          : `MISS(head_dist=${secondaryHeadResult?.dist.toFixed(1)},r=${secondaryHeadResult?.radius.toFixed(1)})`;

    // ── Resolve the best hit (z-order / closest wins) ────────────────────────
    // Priority rules:
    //   1. Head hits beat body hits (head is the scoring zone)
    //   2. Among multiple head hits (genuine overlap), pick the closest to click point
    //   3. Among ties, primary target takes priority (rendered on top / closer to camera)
    if (hits.length > 0) {
      // Sort: head before body, then by distance ascending, then primary before secondary
      hits.sort((a, b) => {
        if (a.zone !== b.zone) return a.zone === 'head' ? -1 : 1;
        if (a.dist !== b.dist) return a.dist - b.dist;
        return a.target === 'primary' ? -1 : 1;
      });

      const best = hits[0];
      const outcome = `${best.target.toUpperCase()}_${best.zone.toUpperCase()}_HIT`;

      console.log(
        `[AimTrainer] t=${clickTime} | src=${coordSource} | click=(${cx.toFixed(1)},${cy.toFixed(1)}) | ` +
        `activeTgts=${activeTargetCount} | primary=${primaryHitLabel} | secondary=${secondaryHitLabel} | ` +
        `outcome=${outcome} | roundState=${roundStateLogRef.current}`
      );

      if (best.target === 'primary') {
        onTargetClick(best.zone);
      } else {
        onSecondTargetClick!(best.zone);
      }
      return;
    }

    // ── No hit on any target — dispatch miss to the appropriate target(s) ────
    if (primaryVisible) {
      console.log(
        `[AimTrainer] t=${clickTime} | src=${coordSource} | click=(${cx.toFixed(1)},${cy.toFixed(1)}) | ` +
        `activeTgts=${activeTargetCount} | primary=${primaryHitLabel} | secondary=${secondaryHitLabel} | ` +
        `outcome=PRIMARY_MISS | roundState=${roundStateLogRef.current}`
      );
      onTargetClick('miss');
      return;
    }

    if (secondaryVisible && onSecondTargetClick) {
      console.log(
        `[AimTrainer] t=${clickTime} | src=${coordSource} | click=(${cx.toFixed(1)},${cy.toFixed(1)}) | ` +
        `activeTgts=${activeTargetCount} | primary=${primaryHitLabel} | secondary=${secondaryHitLabel} | ` +
        `outcome=SECONDARY_MISS | roundState=${roundStateLogRef.current}`
      );
      onSecondTargetClick('miss');
      return;
    }

    // No target visible at all
    console.log(
      `[AimTrainer] t=${clickTime} | src=${coordSource} | click=(${cx.toFixed(1)},${cy.toFixed(1)}) | ` +
      `activeTgts=0 | outcome=NO_TARGET_VISIBLE | roundState=${roundStateLogRef.current}`
    );

    onSceneClick();
  }, [onTargetClick, onSecondTargetClick, onSceneClick, aimDetectionSource]);

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      onMouseDown={handleSceneMouseDown}
      tabIndex={0}
      style={{ cursor: 'crosshair', outline: 'none' }}
    >
      {/* Sky / background */}
      {map.backgroundImage ? (
        <div
          key={map.backgroundImage}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${map.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            imageRendering: 'auto',
          }}
        />
      ) : isIcebox ? (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/images/image-1782909443374.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: map.skyGradient }} />
      )}

      {/* Distant geometry — skip for Icebox (real photo) and background image maps */}
      {!isIcebox && !map.backgroundImage && <MapEnvironment map={map} />}

      {/* Wall that target peeks from — skip for Icebox and background image maps */}
      {!isIcebox && !map.backgroundImage && <WallGeometry map={map} />}

      {/* Floor — skip for Icebox and background image maps */}
      {!isIcebox && !map.backgroundImage && (
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{ height: `${map.floorHeightPercent}%`, background: map.floorGradient }}
        />
      )}

      {/*
        Primary Target — wrapped in a cover-occlusion clip container.
        The clip container uses overflow:hidden to mask the portion of the target
        that is still "behind" the cover edge. Only the part that has moved past
        the cover edge (the wall/box corner) is visible — creating a peek effect.

        Cover edge position (viewport-relative):
          - left wall  → cover edge is at the target's LEFT edge when centered:
                         left = 50% - headOffsetPx + spawnOffsetX
                         Clip container spans from this edge to the right (100vw).
          - right wall → cover edge is at the target's RIGHT edge when centered:
                         right = 50% + headOffsetPx + spawnOffsetX
                         Clip container spans from left (0) to this edge.

        The TargetSilhouette animation (translateX) moves the sprite inside this
        container. At spawn the sprite is fully hidden behind the cover; as it
        moves past the edge, progressively more is revealed.
      */}
      {isTargetVisible && (() => {
        // Cover edge x in viewport-relative terms (px from left edge of viewport)
        // headOffsetPx = bodyW / 2 = half the sprite width, used to center the sprite on 50%
        // spawnOffsetX shifts the whole anchor point
        const coverEdgePx = effectiveSpawnSide === 'left'
          // Left wall: cover right edge = 50%vw - headOffsetPx + spawnOffsetX
          // (this is where the target's left edge sits when at center)
          ? `calc(50% - ${headOffsetPx}px + ${spawnOffsetX}px)`
          // Right wall: cover left edge = 50%vw + headOffsetPx + spawnOffsetX
          // (this is where the target's right edge sits when at center)
          : `calc(50% + ${headOffsetPx}px + ${spawnOffsetX}px)`;

        // Clip container style — spans from cover edge to the open side
        const clipContainerStyle: React.CSSProperties = effectiveSpawnSide === 'left'
          ? {
              // Left wall: reveal region is to the RIGHT of the cover edge
              position: 'absolute',
              left: coverEdgePx,
              right: 0,
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 10,
            }
          : {
              // Right wall: reveal region is to the LEFT of the cover edge
              position: 'absolute',
              left: 0,
              right: `calc(100% - (${coverEdgePx}))`,
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 10,
            };

        // The target anchor inside the clip container.
        // We need to re-anchor so the target's center (50%vw) maps correctly
        // inside the clip container's coordinate space.
        // The clip container's left edge IS the cover edge, so:
        //   - left wall: target left = 0 (cover edge = target left edge at center)
        //   - right wall: target left = coverEdgePx - bodyW (cover edge = target right edge at center)
        // Then the TargetSilhouette translateX animation moves it from behind the cover into view.
        const targetAnchorStyle: React.CSSProperties = effectiveSpawnSide === 'left'
          ? {
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: `translateY(calc(-${headHalfH}px - ${spawnOffsetY}px))`,
              pointerEvents: 'none',
            }
          : {
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: `translateY(calc(-${headHalfH}px - ${spawnOffsetY}px))`,
              pointerEvents: 'none',
            };

        return (
          <div style={clipContainerStyle}>
            <div style={targetAnchorStyle}>
              <TargetSilhouette
                map={map}
                roundState={roundState}
                targetScale={targetScale}
                debugHitbox={showDebugHitboxes}
                headRef={headRef}
                bodyRef={bodyRef}
                characterSpeed={characterSpeed}
                travelDistance={travelDistance}
                targetMotion={targetMotion}
                spawnDirection={effectiveSpawnSide}
                targetSkin={targetSkin}
                targetSkinWeapon={targetSkinWeapon}
                advancedHitbox={advancedHitbox}
              />
            </div>
          </div>
        );
      })()}

      {/*
        Second Target (Simulation Mode) — same cover-occlusion clip logic,
        using effectiveSecondSpawnSide for its own independent cover edge.
      */}
      {isSecondTargetVisible && (() => {
        const coverEdgePx2 = effectiveSecondSpawnSide === 'left'
          ? `calc(50% - ${headOffsetPx}px + ${spawnOffsetX}px)`
          : `calc(50% + ${headOffsetPx}px + ${spawnOffsetX}px)`;

        const clipContainerStyle2: React.CSSProperties = effectiveSecondSpawnSide === 'left'
          ? {
              position: 'absolute',
              left: coverEdgePx2,
              right: 0,
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 10,
            }
          : {
              position: 'absolute',
              left: 0,
              right: `calc(100% - (${coverEdgePx2}))`,
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 10,
            };

        const targetAnchorStyle2: React.CSSProperties = effectiveSecondSpawnSide === 'left'
          ? {
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: `translateY(calc(-${headHalfH}px - ${spawnOffsetY}px))`,
              pointerEvents: 'none',
            }
          : {
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: `translateY(calc(-${headHalfH}px - ${spawnOffsetY}px))`,
              pointerEvents: 'none',
            };

        return (
          <div style={clipContainerStyle2}>
            <div style={targetAnchorStyle2}>
              <TargetSilhouette
                map={map}
                roundState={secondRoundState}
                targetScale={targetScale}
                debugHitbox={showDebugHitboxes}
                headRef={head2Ref}
                bodyRef={body2Ref}
                characterSpeed={characterSpeed}
                travelDistance={travelDistance}
                targetMotion={targetMotion}
                spawnDirection={effectiveSecondSpawnSide}
                targetSkin={targetSkin}
                targetSkinWeapon={targetSkinWeapon}
                advancedHitbox={advancedHitbox}
              />
            </div>
          </div>
        );
      })()}

      {/* Centered crosshair — only visible after Press Start fades */}
      {showCrosshair && (
        <CenteredCrosshair size={crosshairSize} color={crosshairColor} style={crosshairStyle} />
      )}

      {/* Debug mode indicator */}
      {showDebugHitboxes && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: 8,
            right: 8,
            background: 'rgba(0,0,0,0.7)',
            color: '#FF0',
            fontSize: 11,
            fontFamily: 'monospace',
            padding: '3px 8px',
            borderRadius: 4,
            border: '1px solid #FF0',
            zIndex: 50,
          }}
        >
          DEBUG HITBOX ON | console logging active
        </div>
      )}

      {/* Wall-side debug label — visible when debug hitboxes are enabled */}
      {showDebugHitboxes && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: 36,
            right: 8,
            background: 'rgba(0,0,0,0.7)',
            color: '#00FF94',
            fontSize: 11,
            fontFamily: 'monospace',
            padding: '3px 8px',
            borderRadius: 4,
            border: '1px solid #00FF94',
            zIndex: 50,
          }}
        >
          Wall Side: {effectiveSpawnSide.charAt(0).toUpperCase() + effectiveSpawnSide.slice(1)}
          {spawnDirection !== undefined ? ' (override)' : ' (auto)'}
        </div>
      )}
    </div>
  );
}

/** Crosshair fixed at the exact center of the viewport */
export function CenteredCrosshair({ size, color, style }: { size: number; color: string; style: CrosshairStyle }) {
  if (style === 'dot') {
    const dotSize = Math.max(4, Math.round(size * 0.8));
    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 15,
        }}
      >
        <div
          style={{
            width: `${dotSize}px`,
            height: `${dotSize}px`,
            background: color,
            border: '1px solid #000000',
            borderRadius: '50%',
            boxSizing: 'border-box',
          }}
        />
      </div>
    );
  }

  const gap = size * 2.5;
  const lineLen = size * 4;
  const thickness = Math.max(1, size * 0.5);

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 15,
      }}
    >
      <div
        className="absolute rounded-full"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: color,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          boxShadow: `0 0 ${size * 2}px ${color}`,
        }}
      />
      <div className="absolute" style={{ width: `${thickness}px`, height: `${lineLen}px`, background: color, top: `calc(50% - ${gap + lineLen}px)`, left: `calc(50% - ${thickness / 2}px)`, boxShadow: `0 0 4px ${color}60` }} />
      <div className="absolute" style={{ width: `${thickness}px`, height: `${lineLen}px`, background: color, top: `calc(50% + ${gap}px)`, left: `calc(50% - ${thickness / 2}px)`, boxShadow: `0 0 4px ${color}60` }} />
      <div className="absolute" style={{ height: `${thickness}px`, width: `${lineLen}px`, background: color, left: `calc(50% - ${gap + lineLen}px)`, top: `calc(50% - ${thickness / 2}px)`, boxShadow: `0 0 4px ${color}60` }} />
      <div className="absolute" style={{ height: `${thickness}px`, width: `${lineLen}px`, background: color, left: `calc(50% + ${gap}px)`, top: `calc(50% - ${thickness / 2}px)`, boxShadow: `0 0 4px ${color}60` }} />
    </div>
  );
}

function MapEnvironment({ map }: { map: MapConfig }) {
  return (
    <>
      {map.environmentElements.map((el) => (
        <div
          key={`env-${el.id}`}
          className="absolute"
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`,
            width: `${el.w}%`,
            height: `${el.h}%`,
            background: el.color,
            borderRadius: el.radius ?? 0,
            opacity: el.opacity ?? 1,
            zIndex: el.zIndex ?? 1,
          }}
        />
      ))}
    </>
  );
}

function WallGeometry({ map }: { map: MapConfig }) {
  return (
    <>
      {map.wallElements.map((wall) => (
        <div
          key={`wall-${wall.id}`}
          className="absolute"
          style={{
            left: wall.left !== undefined ? `${wall.left}%` : 'auto',
            right: wall.right !== undefined ? `${wall.right}%` : 'auto',
            top: wall.top !== undefined ? `${wall.top}%` : 'auto',
            bottom: wall.bottom !== undefined ? `${wall.bottom}%` : 'auto',
            width: wall.wCalc ?? `${wall.w}%`,
            height: `${wall.h}%`,
            background: wall.color,
            zIndex: wall.zIndex ?? 5,
            borderRadius: wall.radius ?? 0,
          }}
        />
      ))}
    </>
  );
}